import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Eyepatch } from '@/beta/components/Eyepatch';
import { useDragScrub } from '@/beta/hooks/useDragScrub';
import type { PhaseEntry } from '@/beta/types/showcase';

interface ScrubberProps {
  phases: PhaseEntry[];
  activeIndex: number;
  alt: string;
  onPhaseChange: (index: number) => void;
}

const CROSS_DISSOLVE_MS = 350;

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(m.matches);
    const onChange = () => setReduced(m.matches);
    m.addEventListener?.('change', onChange);
    return () => m.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

/** Cinematic full-bleed photo container with horizontal drag-to-scrub +
 *  layered crossfade between phase images. The drag updates a live
 *  `dragIndex` for visual feedback; the committed index lives upstream
 *  and propagates back via `onPhaseChange` on pointer release. */
export function Scrubber({ phases, activeIndex, alt, onPhaseChange }: ScrubberProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const reducedMotion = usePrefersReducedMotion();
  const [prevSrc, setPrevSrc] = useState<string | null>(null);
  const lastIndexRef = useRef(activeIndex);

  // Track container width for the drag math.
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => setWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // When the active index changes, capture the previous src and fade it out.
  useEffect(() => {
    if (lastIndexRef.current === activeIndex) return;
    const prev = phases[lastIndexRef.current]?.hero_image_url ?? null;
    lastIndexRef.current = activeIndex;
    if (reducedMotion || !prev) {
      setPrevSrc(null);
      return;
    }
    setPrevSrc(prev);
    const t = setTimeout(() => setPrevSrc(null), CROSS_DISSOLVE_MS);
    return () => clearTimeout(t);
  }, [activeIndex, phases, reducedMotion]);

  const drag = useDragScrub({
    phaseCount: phases.length,
    currentIndex: activeIndex,
    width,
    onCommit: onPhaseChange,
  });

  const showIndex = drag.isDragging ? drag.dragIndex : activeIndex;
  const activeEntry = phases[showIndex];
  const activeSrc = activeEntry?.hero_image_url ?? null;

  return (
    <div
      ref={containerRef}
      data-scrubber-surface
      data-reduced-motion={reducedMotion}
      data-dragging={drag.isDragging}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: 'var(--vm-dark)',
        userSelect: 'none',
        touchAction: 'pan-y',
      }}
      onPointerDown={drag.onPointerDown}
      onPointerMove={drag.onPointerMove}
      onPointerUp={drag.onPointerUp}
      onPointerCancel={drag.onPointerCancel}
    >
      {/* Outgoing layer (only present mid-crossfade) */}
      {prevSrc ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0,
            transition: `opacity ${CROSS_DISSOLVE_MS}ms ease`,
          }}
        >
          <Eyepatch src={prevSrc} alt={alt} />
        </div>
      ) : null}

      {/* Incoming layer */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 1,
          transition: reducedMotion ? 'none' : `opacity ${CROSS_DISSOLVE_MS}ms ease`,
        }}
      >
        <Eyepatch src={activeSrc} alt={alt} />
      </div>
    </div>
  );
}
